import { ErrorModal, useMesfRealtime } from "@dexteel/mesf-core";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";

import { Grade } from "../models/Grade";
import { getGrades } from "../repositories/GradeRepository";

type UseGradesProps = {
	includeInactive?: boolean;
	refetchInterval?: number;
};

export const useGrades = ({
	includeInactive = false,
	refetchInterval,
}: UseGradesProps = {}) => {
	return useQuery<Grade[], Error>({
		queryKey: ["grades", includeInactive],
		queryFn: ({ signal }) => getGrades({ includeInactive }, signal),
		refetchInterval,
	});
};

type Props = {
	label?: string;
	value: number | null;
	onChange?: (value: number | null, name?: string | null) => void;
	disabled?: boolean;
	required?: boolean;
	fieldError?: FieldError;
	disabledGradeIds?: number[];
};

export const GradeSelector = ({
	label = "Grade",
	value,
	onChange = () => {},
	disabled = false,
	required = false,
	fieldError,
	disabledGradeIds,
}: Props) => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");

	const {
		data: grades,
		isLoading,
		error: e,
		isError,
	} = useGrades({
		refetchInterval: 60_000,
	});

	const selectedValue = grades?.find((g) => g.GradeId === value) || null;

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	useMesfRealtime({
		onReceiveMessage: async (_author, message) => {
			const clean = message.replaceAll("[", "").replaceAll("]", "");
			if (clean === "M2.GetGrades") {
				await queryClient.invalidateQueries({ queryKey: ["grades"] });
			}
		},
	});

	return (
		<>
			<Autocomplete
				size="small"
				clearOnEscape
				autoHighlight
				options={grades || []}
				getOptionLabel={(option) => option.GradeName}
				getOptionDisabled={(option) =>
					!!disabledGradeIds?.includes(option.GradeId)
				}
				disabled={disabled}
				value={selectedValue}
				onChange={(_event, newValue) => {
					onChange(newValue?.GradeId ?? null, newValue?.GradeName ?? null);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						margin="none"
						sx={{
							backgroundColor: "white",
						}}
						inputProps={{
							...params.inputProps,
							"data-test-id": "grade-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading grades"
			/>
		</>
	);
};
